type RequestGuard = {
  beginRequest: () => number;
  isLatestRequest: (requestId: number) => boolean;
  /** Told whether the latest request failed, so the page can flag stale rows. */
  setLoadFailed?: (failed: boolean) => void;
};

/**
 * Runs a list fetch and applies the result only if no newer request has started.
 * Prevents out-of-order responses (e.g. "app" overwriting "apple") from updating the table.
 *
 * A `null`/`undefined` result counts as a failed load: `adminApiService` shows
 * its own error toast and resolves `undefined` instead of throwing. The rows on
 * screen are kept either way, and the guard's `loadFailed` flag is raised so
 * the table can say they may be out of date.
 */
export async function guardedListFetch<T>(
  guard: RequestGuard,
  setLoading: (loading: boolean) => void,
  fetchFn: () => Promise<T | undefined>,
  applyResult: (result: T) => void,
  onError?: (error: unknown) => void,
): Promise<void> {
  const requestId = guard.beginRequest();
  setLoading(true);
  try {
    const result = await fetchFn();
    if (!guard.isLatestRequest(requestId)) return;
    if (result == null) {
      guard.setLoadFailed?.(true);
      return;
    }
    guard.setLoadFailed?.(false);
    applyResult(result);
  } catch (error) {
    if (!guard.isLatestRequest(requestId)) return;
    guard.setLoadFailed?.(true);
    if (onError) {
      onError(error);
    } else {
      console.error("List fetch failed", error);
    }
  } finally {
    if (guard.isLatestRequest(requestId)) {
      setLoading(false);
    }
  }
}
